/**
 * The `pipeline-mgmt-api` grouping-rules port — the authoring surface for the
 * `tx_grouping` engine's declarative relationship-spec overrides.
 *
 * The engine resolves a precedence ladder per transaction (system default →
 * customer → org → tenant → team → user) and applies **one winner per
 * `saga_kind`**: a tenant rule REPLACES the org rule it shadows rather than
 * blending with it, because two enabled rules on one kind would attach the same
 * leg to two groups. That is the single most important thing for an author to
 * understand, and it is why the list surfaces `scope` and `priority` next to
 * every row.
 *
 * `matchCount` / `lastMatchedAt` are what let this surface answer "is this rule
 * working at all". A rule whose `spec` fails to validate is skipped by the
 * engine with a server-side log and stays `enabled: true`, so without the
 * counters a silently-broken rule and a perfectly-working one look identical.
 *
 * Speaks canonical `/grouping/rules` (mapped to `private/pipeline-mgmt-api`).
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

/** Precedence tiers, most general first — the ladder the engine resolves. */
export const GROUPING_RULE_SCOPES = [
  "system",
  "customer",
  "org",
  "tenant",
  "team",
  "user",
] as const;
export type GroupingRuleScope = (typeof GROUPING_RULE_SCOPES)[number];

export interface GroupingRuleSummary {
  id: string;
  name: string;
  description?: string | null;
  scope: GroupingRuleScope;
  enabled: boolean;
  /** Lower wins when several rules target the same `sagaKind`. */
  priority: number;
  sagaKind?: string | null;
  relationshipType?: string | null;
  chainIds: string[];
  /** The engine's `RelationshipSpec` body, as authored. */
  spec: Record<string, unknown>;
  /** Times the engine credited a formed group to this rule. */
  matchCount: number;
  /** Unix seconds of the most recent match, when there has been one. */
  lastMatchedAt?: number | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
}

export interface SaveGroupingRuleInput {
  name: string;
  description?: string | null;
  scope?: GroupingRuleScope;
  enabled?: boolean;
  priority?: number;
  sagaKind?: string | null;
  chainIds?: string[];
  spec?: Record<string, unknown>;
}

/** One selectable option, as the backend labels it. */
export interface CatalogOption {
  value: string;
  label: string;
  description?: string;
}

/** A model field the builder renders a control for. */
export interface CatalogField {
  name: string;
  /** Coarse wire type: `string` / `integer` / `boolean` / `string[]`. */
  type: string;
  required: boolean;
  /** The model's own prose, one sentence, markup stripped. */
  description?: string;
}

/**
 * The authoring vocabulary, read off the live models by
 * `commons.grouping.spec_catalog` and served at `GET /grouping/spec-catalog`.
 *
 * Fetched rather than hardcoded on purpose: a frontend carrying its own
 * `["deterministic", "semi_deterministic", "probabilistic"]` silently omits the
 * fourth linkage model the day somebody adds one, and keeps offering a removed
 * one until a human notices.
 */
export interface SpecCatalog {
  relationshipTypes: CatalogOption[];
  linkageModels: CatalogOption[];
  truthLocations: CatalogOption[];
  transforms: CatalogOption[];
  defaultTransform: string;
  roles: { canonical: string[]; known: string[]; defaultCompleteRoles: string[] };
  specFields: CatalogField[];
  selectorFields: CatalogField[];
  valueSourceFields: CatalogField[];
}

export interface GroupingRulesSource {
  /** The authoring vocabulary. Static — cache it for the session. */
  specCatalog(): Promise<SpecCatalog>;
  list(): Promise<GroupingRuleSummary[]>;
  create(input: SaveGroupingRuleInput): Promise<GroupingRuleSummary>;
  update(id: string, input: Partial<SaveGroupingRuleInput>): Promise<GroupingRuleSummary>;
  remove(id: string): Promise<void>;
}

function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function ruleFromWire(raw: Record<string, any>): GroupingRuleSummary {
  return {
    id: String(raw.id ?? ""),
    name: String(raw.name ?? ""),
    description: raw.description ?? null,
    scope: (raw.scope ?? "tenant") as GroupingRuleScope,
    enabled: raw.enabled !== false,
    priority: num(raw.priority, 100),
    sagaKind: raw.saga_kind ?? null,
    relationshipType: raw.relationship_type ?? null,
    chainIds: Array.isArray(raw.chain_ids) ? raw.chain_ids.map(String) : [],
    spec: (raw.spec ?? {}) as Record<string, unknown>,
    matchCount: num(raw.match_count, 0),
    lastMatchedAt: typeof raw.last_matched_at === "number" ? raw.last_matched_at : null,
    updatedAt: typeof raw.updated_at === "number" ? raw.updated_at : null,
    updatedBy: raw.updated_by ?? null,
  };
}

function toWire(input: Partial<SaveGroupingRuleInput>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.scope !== undefined) body.scope = input.scope;
  if (input.enabled !== undefined) body.enabled = input.enabled;
  if (input.priority !== undefined) body.priority = input.priority;
  if (input.sagaKind !== undefined) body.saga_kind = input.sagaKind;
  if (input.chainIds !== undefined) body.chain_ids = input.chainIds;
  if (input.spec !== undefined) body.spec = input.spec;
  return body;
}

function optionsFromWire(raw: unknown): CatalogOption[] {
  return Array.isArray(raw)
    ? raw.map((o: any) => ({
        value: String(o?.value ?? ""),
        label: String(o?.label ?? o?.value ?? ""),
        description: o?.description ?? undefined,
      }))
    : [];
}

function fieldsFromWire(raw: unknown): CatalogField[] {
  return Array.isArray(raw)
    ? raw.map((f: any) => ({
        name: String(f?.name ?? ""),
        type: String(f?.type ?? "string"),
        required: f?.required === true,
        description: f?.description ?? undefined,
      }))
    : [];
}

function catalogFromWire(raw: Record<string, any>): SpecCatalog {
  const roles = raw.roles ?? {};
  return {
    relationshipTypes: optionsFromWire(raw.relationship_types),
    linkageModels: optionsFromWire(raw.linkage_models),
    truthLocations: optionsFromWire(raw.truth_locations),
    transforms: optionsFromWire(raw.transforms),
    defaultTransform: String(raw.default_transform ?? "lower"),
    roles: {
      canonical: Array.isArray(roles.canonical) ? roles.canonical.map(String) : [],
      known: Array.isArray(roles.known) ? roles.known.map(String) : [],
      defaultCompleteRoles: Array.isArray(roles.default_complete_roles)
        ? roles.default_complete_roles.map(String)
        : [],
    },
    specFields: fieldsFromWire(raw.spec_fields),
    selectorFields: fieldsFromWire(raw.selector_fields),
    valueSourceFields: fieldsFromWire(raw.value_source_fields),
  };
}

export function createApiGroupingRulesSource(client: ApiClient): GroupingRulesSource {
  return {
    async specCatalog(): Promise<SpecCatalog> {
      const res = await client.get<Record<string, any>>("/grouping/spec-catalog");
      return catalogFromWire(unwrapData(res) ?? {});
    },

    async list(): Promise<GroupingRuleSummary[]> {
      const res = await client.get<Record<string, any>>("/grouping/rules");
      const data = unwrapData(res);
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      return items.map(ruleFromWire);
    },

    async create(input: SaveGroupingRuleInput): Promise<GroupingRuleSummary> {
      const res = await client.post<Record<string, any>>("/grouping/rules", toWire(input));
      return ruleFromWire(unwrapData(res) ?? {});
    },

    async update(
      id: string,
      input: Partial<SaveGroupingRuleInput>,
    ): Promise<GroupingRuleSummary> {
      const res = await client.patch<Record<string, any>>(
        `/grouping/rules/${encodeURIComponent(id)}`,
        toWire(input),
      );
      return ruleFromWire(unwrapData(res) ?? {});
    },

    async remove(id: string): Promise<void> {
      await client.delete<Record<string, any>>(
        `/grouping/rules/${encodeURIComponent(id)}`,
      );
    },
  };
}
